import React from 'react';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import RichTextEditor from '../editor/RichTextEditor';

const PRICING_PRESETS = [
  { name: "Premium Landing Page", items: [{ description: "Strategic Design & High-Conversion Layout", amount: 1200 }, { description: "Mobile responsiveness & Performance Optimization", amount: 300 }, { description: "Lead Capture & Enquiry Form Integration", amount: 150 }] },
  { name: "Multi-Page Business Website", items: [{ description: "Custom Website Design (Up to 5 Pages)", amount: 2000 }, { description: "Core Page Development & CMS Integration", amount: 2500 }, { description: "Foundational On-Page SEO Setup", amount: 500 }] },
  { name: "AI Chatbot Implementation", items: [{ description: "Custom AI Chatbot Logic & Flow Configuration", amount: 1000 }, { description: "Knowledge Base Source Content Setup", amount: 600 }, { description: "Deployment & Integration Support", amount: 400 }] },
  { name: "BPA Automation Build", items: [{ description: "Workflow Analysis & Strategic Design", amount: 1200 }, { description: "Technical Automation Setup & Logic Build", amount: 1500 }, { description: "Systems Integration & Testing Phase", amount: 500 }] },
];

export default function ProposalFinancials({
  proposal,
  settings,
  currency,
  total,
  subtotal,
  taxAmount,
  totalMilestonePercent,
  showPresets,
  onTogglePresets,
  onApplyPreset,
  onTaxToggle,
  onFinancialChange,
  onAddFinancialItem,
  onRemoveFinancialItem,
  onMilestoneChange,
  onAddMilestone,
  onRemoveMilestone,
  onCopyMilestones,
  onCommercialNotesChange,
}) {
  return (
    <div className="bg-white rounded-2xl border-2 border-indigo-50 shadow-md overflow-visible relative">
      <div className="bg-slate-900 px-4 sm:px-6 py-4 sm:py-5 text-white flex justify-between items-center rounded-t-[14px]">
        <h3 className="font-black flex items-center gap-3 text-sm uppercase tracking-widest">
          <SafeIcon icon={FiIcons.FiDollarSign} /> 3. Commercial Investment
        </h3>
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="relative">
            <button onClick={onTogglePresets} className="flex items-center gap-2 text-[10px] font-black cursor-pointer bg-indigo-500 px-3 sm:px-4 py-2 rounded-lg hover:bg-indigo-400 uppercase tracking-widest transition-colors shadow-sm">
              <SafeIcon icon={FiIcons.FiGrid} /> Presets
            </button>
            {showPresets && (
              <div className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 shadow-2xl rounded-xl z-[100] overflow-hidden">
                <div className="p-3 border-b border-gray-100 bg-gray-50 text-[10px] font-black text-gray-400 uppercase tracking-widest">Select Package</div>
                {PRICING_PRESETS.map(preset => (
                  <button key={preset.name} onClick={() => onApplyPreset(preset)} className="w-full text-left px-4 py-3 text-xs font-bold text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 transition-colors border-b border-gray-50 last:border-0">
                    {preset.name}
                  </button>
                ))}
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 sm:gap-3 text-[10px] font-black cursor-pointer bg-white/10 px-3 sm:px-4 py-2 rounded-lg hover:bg-white/20 uppercase tracking-widest">
            <input type="checkbox" className="w-4 h-4 rounded text-indigo-500 focus:ring-0" checked={proposal.financials.taxEnabled} onChange={e => onTaxToggle(e.target.checked)} />
            {settings.taxName}
          </label>
        </div>
      </div>

      <div className="hidden sm:block">
        <table className="w-full">
          <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b border-gray-100">
            <tr>
              <th className="px-8 py-4 text-left">Deliverable / Milestone</th>
              <th className="px-8 py-4 w-48 text-right pr-6">Amount</th>
              <th className="px-8 py-4 w-12"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {proposal.financials.items.map((item, index) => (
              <tr key={item.id} className="group hover:bg-indigo-50/10">
                <td className="px-8 py-3">
                  <input type="text" value={item.description} onChange={e => onFinancialChange(index, 'description', e.target.value)} className="w-full border border-transparent group-hover:border-gray-200 bg-transparent rounded-lg px-3 py-2 focus:bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all placeholder-gray-300" placeholder="e.g. Website Strategy & Design" />
                </td>
                <td className="px-8 py-3 pr-6">
                  <div className="flex items-center justify-end bg-gray-50 border border-gray-200 rounded-lg px-3 py-2 group-hover:bg-white focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all">
                    <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter mr-2">{currency}</span>
                    <input type="number" value={item.amount} onChange={e => onFinancialChange(index, 'amount', e.target.value)} className="w-full border-none focus:ring-0 p-0 bg-transparent text-right font-black text-gray-900" placeholder="0.00" />
                  </div>
                </td>
                <td className="px-8 py-3 text-right">
                  <button onClick={() => onRemoveFinancialItem(index)} className="p-2 text-gray-300 hover:text-red-500 transition-colors">
                    <SafeIcon icon={FiIcons.FiTrash2} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="sm:hidden space-y-3 p-4">
        {proposal.financials.items.map((item, index) => (
          <div key={item.id} className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Deliverable</label>
              <input type="text" value={item.description} onChange={e => onFinancialChange(index, 'description', e.target.value)} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-indigo-500 outline-none text-sm" placeholder="e.g. Website Strategy & Design" />
            </div>
            <div>
              <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Amount</label>
              <div className="flex items-center bg-white border border-gray-200 rounded-lg px-3 py-2 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500">
                <span className="text-[10px] font-black text-gray-400 uppercase tracking-tighter mr-2">{currency}</span>
                <input type="number" value={item.amount} onChange={e => onFinancialChange(index, 'amount', e.target.value)} className="w-full border-none focus:ring-0 p-0 bg-transparent text-right font-black text-gray-900" placeholder="0.00" />
              </div>
            </div>
            <button onClick={() => onRemoveFinancialItem(index)} className="flex items-center gap-2 text-xs font-bold text-red-500 hover:text-red-700 transition-colors">
              <SafeIcon icon={FiIcons.FiTrash2} /> Remove
            </button>
          </div>
        ))}
      </div>

      <div className="p-4 sm:p-6 border-t border-gray-100 flex justify-between bg-slate-50 items-center">
        <button onClick={onAddFinancialItem} className="text-[10px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2 hover:bg-white px-4 py-2 rounded-lg border border-transparent hover:border-indigo-100 transition-all">
          <SafeIcon icon={FiIcons.FiPlusSquare} /> Add Item
        </button>
        <div className="text-right">
          <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">Total Project Investment</div>
          <div className="text-xl font-black text-indigo-900">{currency} {total.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
        </div>
      </div>

      <div className="border-t border-gray-100 bg-white p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h4 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Payment Milestones</h4>
          <div className="flex items-center gap-2 sm:gap-3">
            <button onClick={onCopyMilestones} className="text-[9px] font-black text-slate-400 hover:text-indigo-600 uppercase tracking-widest flex items-center gap-1.5 transition-colors border border-slate-200 hover:border-indigo-200 px-2.5 py-1.5 rounded-lg bg-white shadow-sm">
              <SafeIcon icon={FiIcons.FiCopy} /> Copy
            </button>
            <div className={`text-[10px] font-black uppercase tracking-widest px-3 py-1.5 rounded-full ${totalMilestonePercent === 100 ? 'bg-green-50 text-green-600' : 'bg-amber-50 text-amber-600'}`}>
              Total: {totalMilestonePercent}%{totalMilestonePercent !== 100 && ' (Target 100%)'}
            </div>
          </div>
        </div>
        <div className="space-y-2">
          {proposal.paymentMilestones.map((milestone, idx) => {
            const mEx = subtotal * (milestone.percent / 100);
            const mTax = taxAmount * (milestone.percent / 100);
            const mInc = total * (milestone.percent / 100);
            return (
              <React.Fragment key={milestone.id}>
                <div className="grid grid-cols-[minmax(220px,1.6fr)_140px_100px_90px_110px_32px] gap-3 items-center bg-slate-100/50 p-2.5 rounded-xl border border-slate-200 transition-all min-h-[64px] hover:border-slate-300 shadow-sm hidden lg:grid">
                  <div className="px-2">
                    <input type="text" value={milestone.label} onChange={e => onMilestoneChange(idx, 'label', e.target.value)} className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none transition-all placeholder-slate-400" placeholder="Milestone label..." />
                  </div>
                  <div className="flex items-center bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all">
                    <input type="number" value={milestone.percent} onChange={e => onMilestoneChange(idx, 'percent', e.target.value)} className="w-full border-none p-0 text-right text-sm font-black focus:ring-0 bg-transparent tabular-nums" />
                    <span className="text-[10px] font-black text-slate-400 ml-2">%</span>
                  </div>
                  <div className="text-right px-3 border-l border-slate-200/50">
                    <div className="text-[8px] font-black text-slate-400 uppercase leading-none mb-1.5">Ex {settings.taxName}</div>
                    <div className="text-[11px] font-bold text-slate-700 leading-none tabular-nums">{mEx.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div className="text-right px-3 border-l border-slate-200/50">
                    <div className="text-[8px] font-black text-slate-400 uppercase leading-none mb-1.5">{settings.taxName}</div>
                    <div className="text-[11px] font-bold text-slate-700 leading-none tabular-nums">{mTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  </div>
                  <div className="text-right px-3 border-l border-slate-200/50">
                    <div className="text-[8px] font-black text-indigo-400 uppercase leading-none mb-1.5">Inc {settings.taxName}</div>
                    <div className="text-[11px] font-black text-indigo-700 leading-none tabular-nums">{mInc.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                  </div>
                  <button onClick={() => onRemoveMilestone(idx)} className="text-slate-300 hover:text-red-500 transition-colors flex justify-center h-full items-center">
                    <SafeIcon icon={FiIcons.FiX} className="text-xs" />
                  </button>
                </div>
                <div className="bg-slate-100/50 p-4 rounded-xl border border-slate-200 space-y-3 lg:hidden">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Label</label>
                    <input type="text" value={milestone.label} onChange={e => onMilestoneChange(idx, 'label', e.target.value)} className="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm font-bold text-slate-800 focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 outline-none" placeholder="Milestone label..." />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Percentage</label>
                    <div className="flex items-center bg-white border border-slate-300 rounded-lg px-4 py-2.5 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500">
                      <input type="number" value={milestone.percent} onChange={e => onMilestoneChange(idx, 'percent', e.target.value)} className="w-full border-none p-0 text-right text-sm font-black focus:ring-0 bg-transparent tabular-nums" />
                      <span className="text-[10px] font-black text-slate-400 ml-2">%</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-200">
                    <div>
                      <div className="text-[8px] font-black text-slate-400 uppercase leading-none mb-1">Ex {settings.taxName}</div>
                      <div className="text-xs font-bold text-slate-700 tabular-nums">{mEx.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div className="text-[8px] font-black text-slate-400 uppercase leading-none mb-1">{settings.taxName}</div>
                      <div className="text-xs font-bold text-slate-700 tabular-nums">{mTax.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                    </div>
                    <div>
                      <div className="text-[8px] font-black text-indigo-400 uppercase leading-none mb-1">Inc {settings.taxName}</div>
                      <div className="text-xs font-black text-indigo-700 tabular-nums">{mInc.toLocaleString(undefined, { minimumFractionDigits: 2 })}</div>
                    </div>
                  </div>
                  <button onClick={() => onRemoveMilestone(idx)} className="flex items-center gap-2 text-xs font-bold text-red-500 hover:text-red-700 transition-colors pt-2">
                    <SafeIcon icon={FiIcons.FiX} /> Remove
                  </button>
                </div>
              </React.Fragment>
            );
          })}
        </div>
        <button onClick={onAddMilestone} className="mt-4 text-[9px] font-black text-indigo-600 uppercase tracking-widest flex items-center gap-2 hover:bg-indigo-50 px-4 py-2 rounded-lg border border-transparent hover:border-indigo-100 transition-all">
          <SafeIcon icon={FiIcons.FiPlus} /> Add Milestone
        </button>
      </div>

      <div className="p-4 sm:p-6 border-t border-gray-100 bg-white rounded-b-[14px]">
        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-3">Commercial & Payment Notes</label>
        <RichTextEditor value={proposal.commercialNotes || ''} onChange={val => onCommercialNotesChange(val)} sectionType="financials" />
      </div>
    </div>
  );
}